import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { ScrollPanelModule } from 'primeng/scrollpanel';

export interface OccurrenceAttachment {
  id: number;
  urlImagem: string;
}

export interface OccurrenceData {
  id: number;
  rondaId: number;
  rondaDataInicio: string;
  rondaDataFim: string | null;
  idEscala: number;
  idPosto: number;
  nomePosto: string;
  idEfetivo: number;
  nomeEfetivo: string;
  criticidade: 'ALTA' | 'MÉDIA' | 'BAIXA' | string;
  tipoOcorrencia: string;
  descricaoTipoOutro: string | null;
  dataOcorrencia: string;
  anexos: OccurrenceAttachment[];
  /**
   * GET /api/ocorrencias/{id} não devolve "estado" (só a listagem devolve).
   * Fica opcional porque o componente pai completa este valor a partir da
   * linha da tabela — pode continuar em falta se o detalhe for aberto de
   * outra forma no futuro.
   */
  estado?: string | null;
  estadoEnum?: number | null;
  desfecho?: number | null;
  desfechoDescricao?: string | null;
  acoesTomadas?: string | null;
  dataResolucao?: string | null;
  resolvidoPor?: string | null;
  resolvidoPorId?: number | null;
  entidadesAccionadas?: string | null;
  valorDanosPerdas?: number | null;
  descricaoDanosPerdas?: string | null;
  tempoAteResolucaoMinutos?: number | null;
}

@Component({
  selector: 'app-detalhe-ocorencia',
  standalone: true,
  imports: [
    CommonModule,
    DrawerModule,
    TagModule,
    ButtonModule,
    DividerModule,
    ScrollPanelModule,
    DatePipe
  ],
  templateUrl: './detalhe-ocorrencia.component.html',
  styleUrl: './detalhe-ocorrencia.component.scss'
})
export class DetalheOcorrenciaComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() occurrence: OccurrenceData | null = null;
  @Input() podeFechar = false;
  @Input() podeReabrir = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() fechar = new EventEmitter<void>();
  @Output() reabrir = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['occurrence']) {
      this.brokenImageIds.clear();
    }
  }

  getCriticidadeSeverity(criticidade: any): 'danger' | 'warn' | 'success' | 'info' {
    const val = String(criticidade ?? '').toUpperCase();
    if (val === 'URGENTE' || val === 'ALTA' || val === '1' || val === '3') return 'danger';
    if (val === 'MÉDIA' || val === 'MEDIA' || val === '2') return 'warn';
    if (val === 'BAIXA') return 'success';
    return 'info';
  }

  getEstadoLabel(estado: any): string {
    const estadoEnum = this.occurrence?.estadoEnum;
    if (estadoEnum === 1) return 'Aberta';
    if (estadoEnum === 2) return 'Fechada';
    if (estadoEnum === 3) return 'Em Progresso';
    const val = String(estado ?? '').toUpperCase();
    if (val === 'ABERTO' || val === 'ABERTA') return 'Aberta';
    if (val === 'EM_PROGRESSO') return 'Em Progresso';
    if (val === 'RESOLVIDO' || val === 'FECHADO' || val === 'FECHADA') return 'Fechada';
    return estado ?? '';
  }

  getEstadoSeverity(estado: any): 'danger' | 'warn' | 'success' | 'info' | 'secondary' {
    const estadoEnum = this.occurrence?.estadoEnum;
    if (estadoEnum === 1) return 'warn';
    if (estadoEnum === 2) return 'secondary';
    if (estadoEnum === 3) return 'info';
    const val = String(estado ?? '').toUpperCase();
    if (val === 'ABERTO' || val === 'ABERTA') return 'danger';
    if (val === 'EM_PROGRESSO') return 'warn';
    if (val === 'RESOLVIDO' || val === 'FECHADO' || val === 'FECHADA') return 'success';
    return 'info';
  }

  getDesfechoLabel(v: any): string {
    const desfecho = v ?? this.occurrence?.desfecho;
    const desc = this.occurrence?.desfechoDescricao;
    if (desc) return desc;
    const val = Number(desfecho);
    if (val === 1) return 'Resolvida';
    if (val === 2) return 'Falso alarme';
    if (val === 3) return 'Sem acção necessária';
    if (val === 4) return 'Encaminhada às autoridades';
    return desc || '';
  }

  formatTempoResolucao(minutos: number | null | undefined): string {
    if (minutos === null || minutos === undefined || isNaN(Number(minutos))) return '-';
    const total = Number(minutos);
    if (total < 60) return `${total} min`;
    const horas = Math.floor(total / 60);
    const mins = total % 60;
    if (mins === 0) return `${horas}h`;
    return `${horas}h ${mins}min`;
  }

  onFechar(): void {
    this.fechar.emit();
  }

  onReabrir(): void {
    this.reabrir.emit();
  }

  getTipoIcon(v: any): string {
    const val = String(v ?? '').toUpperCase();
    const map: Record<string, string> = {
      'INTRUSAO': 'ri-alarm-warning-line',
      'AUSENCIA': 'ri-user-unfollow-line',
      'AVARIA': 'ri-settings-4-line',
      'RONDA_INCOMPLETA': 'ri-route-line',
      'SUSPEITO': 'ri-spy-line',
      'ACIDENTE': 'ri-alert-line',
      'OUTRO': 'ri-chat-1-line',
      '1': 'ri-alert-line',
      '2': 'ri-settings-4-line',
      '3': 'ri-alarm-warning-line',
      '4': 'ri-alarm-warning-line',
      '5': 'ri-chat-1-line',
    };
    return map[val] || 'ri-question-line';
  }

  getTipoLabel(v: any): string {
    const val = String(v ?? '').toUpperCase();
    const map: Record<string, string> = {
      'INTRUSAO': 'Intrusão',
      'AUSENCIA': 'Ausência',
      'AVARIA': 'Avaria',
      'RONDA_INCOMPLETA': 'Ronda Incompleta',
      'SUSPEITO': 'Suspeito',
      'ACIDENTE': 'Acidente',
      'OUTRO': 'Outro',
      '1': 'Incidente',
      '2': 'Falha Equipamento',
      '3': 'Intrusão',
      '4': 'Emergência',
      '5': 'Outro',
    };
    return map[val] || val;
  }

  brokenImageIds = new Set<number>();

  onImageError(id: number): void {
    this.brokenImageIds.add(id);
  }

  openImage(url: string): void {
    window.open(url, '_blank');
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  trackByImageId(index: number, image: OccurrenceAttachment): number {
    return image.id;
  }
}